import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { PageHead } from '../../components/design';
import type { HelpGroup, HelpSlug } from '../../help/types';
import { HELP_ORDER, HELP_TOPICS, START_HERE } from '../../help/topics';
import {
  helpResultPath,
  highlightSegments,
  searchHelp,
  type HelpSearchResult,
} from '../../lib/helpSearch';

// 'Start here' is deliberately absent — that group renders only in the
// onboarding strip above, so its card never shows twice on the page.
const GROUPS: HelpGroup[] = ['Daily work', 'Billing', 'Reference'];

/**
 * As-you-type search on a bundled index is fast enough to run on every
 * keystroke, but re-rendering the whole result list mid-word makes the list
 * flicker through nonsense states ("mo", "mon", "mone") the reader never meant
 * to search. A short delay lets the word finish.
 */
function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return settled;
}

const SEARCH_DEBOUNCE_MS = 140;

function TopicCard({ slug }: { slug: HelpSlug }) {
  const t = HELP_TOPICS[slug];
  if (!t) return null; // topic not authored yet — card simply absent
  return (
    <Link to={`/help/${t.slug}`} className="help-card">
      <span className="help-card-title">{t.title}</span>
      <span className="help-card-summary">{t.summary}</span>
    </Link>
  );
}

/**
 * The full topic index, grouped. Rendered when nothing is being searched — and
 * also underneath a no-results message, so a query that finds nothing still
 * leaves the reader looking at every topic rather than at a dead end.
 */
function TopicGroups({ showStartHere }: { showStartHere: boolean }) {
  return (
    <>
      {showStartHere && (
        <section className="section section--card section--start-here">
          <h2 className="section-title">New to the portal? Start here</h2>
          <div className="help-index-grid">
            {START_HERE.map((slug) => (
              <TopicCard key={slug} slug={slug} />
            ))}
          </div>
        </section>
      )}

      {GROUPS.map((group) => {
        const slugs = HELP_ORDER.filter((s) => HELP_TOPICS[s]?.group === group);
        if (slugs.length === 0) return null;
        return (
          <section key={group} className="section section--card">
            <h2 className="section-title">{group}</h2>
            <div className="help-index-grid">
              {slugs.map((slug) => (
                <TopicCard key={slug} slug={slug} />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

/** The words the result matched on, marked in place. */
function Highlighted({ text, terms }: { text: string; terms: readonly string[] }) {
  return (
    <>
      {highlightSegments(text, terms).map((seg, i) =>
        seg.hit ? <mark key={i}>{seg.text}</mark> : <span key={i}>{seg.text}</span>,
      )}
    </>
  );
}

const KIND_LABEL: Record<HelpSearchResult['kind'], string> = {
  topic: 'Topic',
  recipe: 'How to',
  section: 'Section',
};

/**
 * Search results. Deliberately a listbox: arrow keys move the highlight and
 * Enter opens the highlighted row, so the roles are honest about what the
 * keyboard actually does. Every row is still a real link — click, middle-click,
 * and open-in-new-tab all work.
 */
function ResultRow({
  result,
  index,
  active,
  onHover,
}: {
  result: HelpSearchResult;
  index: number;
  active: boolean;
  onHover: (index: number) => void;
}) {
  return (
    // presentation: a listbox's children must be options, and the option is
    // the link — the li is only here to keep the list markup valid.
    <li role="presentation">
      <Link
        to={helpResultPath(result)}
        id={`help-result-${index}`}
        role="option"
        aria-selected={active}
        className={active ? 'help-result is-active' : 'help-result'}
        onMouseEnter={() => onHover(index)}
      >
        <span className="help-result-eyebrow">
          {result.topicTitle}
          <span className="help-result-kind">{KIND_LABEL[result.kind]}</span>
        </span>
        <span className="help-result-label">
          <Highlighted text={result.label} terms={result.terms} />
        </span>
        {result.snippet && (
          <span className="help-result-snippet">
            <Highlighted text={result.snippet} terms={result.terms} />
          </span>
        )}
      </Link>
    </li>
  );
}

export function HelpIndexPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const settled = useDebounced(query, SEARCH_DEBOUNCE_MS);
  const results = useMemo(() => searchHelp(settled), [settled]);
  // Follows the INPUT, not the debounced value: the topic list must not flash
  // back for the 140ms between the first keystroke and the first result.
  const searching = query.trim() !== '';
  const listOpen = searching && results.length > 0;

  // A shrinking result list must not leave the highlight past the end.
  useEffect(() => {
    setActive((a) => (a < results.length ? a : 0));
  }, [results]);

  // Screen-reader announcement. Only the COUNT is announced, and only when it
  // changes — an aria-live region rebuilt on every keystroke talks over the
  // typing it is meant to describe.
  const [announcement, setAnnouncement] = useState('');
  const announcedCount = useRef<number | null>(null);
  useEffect(() => {
    if (!searching) {
      announcedCount.current = null;
      setAnnouncement('');
      return;
    }
    if (announcedCount.current === results.length) return;
    announcedCount.current = results.length;
    setAnnouncement(results.length === 0 ? 'No help results' : `${results.length} help results`);
  }, [searching, results.length]);

  const setQueryAndReset = (next: string) => {
    setQuery(next);
    setActive(0);
  };

  const clear = () => {
    setQueryAndReset('');
    inputRef.current?.focus();
  };

  /** Keep the highlighted row in view without stealing focus from the input. */
  const move = (delta: number) => {
    if (results.length === 0) return;
    const next = (active + delta + results.length) % results.length;
    setActive(next);
    listRef.current?.querySelector(`#help-result-${next}`)?.scrollIntoView({ block: 'nearest' });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      // Escape clears rather than blurs: the query is the only thing standing
      // between the reader and the normal topic list.
      e.preventDefault();
      if (query === '') inputRef.current?.blur();
      else setQueryAndReset('');
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
      return;
    }
    if (e.key === 'Enter') {
      const target = results[active];
      if (!target) return;
      e.preventDefault();
      navigate(helpResultPath(target));
    }
  };

  return (
    <div className="container">
      <PageHead
        title="Help"
        sub="How the portal works, task by task. Every page here is written to be read by you — or by an AI assistant helping you."
      />

      <div className="help-search">
        <Search size={15} strokeWidth={2} aria-hidden className="help-search-ico" />
        <input
          ref={inputRef}
          type="text"
          className="help-search-input"
          placeholder="Search help — try “money looks wrong”, “send to the GC”, “per diem”"
          value={query}
          onChange={(e) => setQueryAndReset(e.target.value)}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={listOpen}
          // Only while the listbox is actually in the DOM — a dangling
          // aria-controls is an invalid attribute value, not a hint.
          aria-controls={listOpen ? 'help-results' : undefined}
          aria-activedescendant={listOpen && results[active] ? `help-result-${active}` : undefined}
          aria-label="Search help topics"
          autoComplete="off"
          spellCheck={false}
        />
        {searching && (
          <button
            type="button"
            className="help-search-clear"
            onClick={clear}
            aria-label="Clear search"
          >
            <X size={14} strokeWidth={2} aria-hidden />
          </button>
        )}
      </div>

      {/* Announced, not shown twice — the count is already visible as the list. */}
      <div className="sr-only" role="status" aria-live="polite">
        {announcement}
      </div>

      {searching && results.length > 0 && (
        <section className="section section--card">
          <ul
            className="help-results"
            id="help-results"
            role="listbox"
            ref={listRef}
            aria-label="Help search results"
          >
            {results.map((r, i) => (
              <ResultRow
                key={`${r.topicSlug}-${r.kind}-${r.anchor ?? ''}-${i}`}
                result={r}
                index={i}
                active={i === active}
                onHover={setActive}
              />
            ))}
          </ul>
        </section>
      )}

      {searching && results.length === 0 && (
        <section className="section section--card">
          <p className="help-search-empty">
            Nothing in the help matches “{query.trim()}”. Try fewer words, or a different one — the{' '}
            <Link to="/help/glossary">Glossary</Link> defines every term the portal uses. Every
            topic is listed below.
          </p>
        </section>
      )}

      {(!searching || results.length === 0) && <TopicGroups showStartHere={!searching} />}
    </div>
  );
}
